import {
  HR_WORKER_ONLY_GO_LIVE_SCOPE,
  RAC_CORE_GO_LIVE_SCOPE,
  SAH_CORE_GO_LIVE_SCOPE,
  normalizeGoLiveScope,
  type VisibleGoLiveScope,
} from './billing';

const COMMON_WORKER_NAV_PATHS = [
  '/overview',
  '/tasks',
  '/tasks/all',
  '/me/profile',
  '/me/roster',
  '/me/timesheets',
  '/me/leave',
  '/me/training',
  '/me/courses',
  '/me/compliance',
  '/me/onboarding',
  '/me/policies',
  '/me/preferences',
  '/me/renewals',
  '/people',
  '/people/workers',
  '/people/onboarding',
  '/people/compliance',
  '/people/learning',
  '/people/policies-conduct',
  '/roster',
  '/settings',
  '/settings/facilities',
  '/settings/facility-config',
  '/settings/modules',
  '/settings/notification-preferences',
  '/settings/notification-routing',
  '/settings/notifications',
  '/settings/organisation',
  '/settings/registration-providers',
  '/settings/roster',
  '/settings/security',
  '/settings/users',
  '/settings/workflows',
  '/help',
] as const;

const COMMON_CARE_NAV_PATHS = [
  '/clients',
  '/care-plans',
  '/assessments',
  '/compliance',
  '/compliance/incidents',
  '/compliance/complaints',
  '/compliance/continuous-improvement',
  '/compliance/quality-indicators',
  '/compliance/audit-program',
] as const;

const LAUNCH_PRIMARY_NAV_PATHS = {
  [HR_WORKER_ONLY_GO_LIVE_SCOPE]: [
    '/overview',
    '/tasks',
    '/tasks/all',
    '/me/profile',
    '/me/onboarding',
    '/me/training',
    '/me/courses',
    '/me/compliance',
    '/me/policies',
    '/me/preferences',
    '/people',
    '/people/workers',
    '/people/onboarding',
    '/people/compliance',
    '/people/learning',
    '/people/policies-conduct',
    '/settings',
    '/settings/modules',
    '/settings/onboarding',
    '/settings/onboarding-packs',
    '/settings/organisation',
    '/settings/security',
    '/settings/users',
    '/settings/workflows',
    '/help',
  ],
  [SAH_CORE_GO_LIVE_SCOPE]: [
    ...COMMON_WORKER_NAV_PATHS,
    ...COMMON_CARE_NAV_PATHS,
    '/cm-tasks/new',
    '/dashboards/home-care-ops',
    '/home-care/service-delivery',
    '/home-care/readiness',
    '/home-care/budgets',
    '/home-care/funding-changes',
    '/home-care/claims',
    '/home-care/statements',
    '/home-care/reconciliation',
    '/home-care/schedules',
    '/finance/invoices',
    '/pricing/rate-cards',
  ],
  [RAC_CORE_GO_LIVE_SCOPE]: [
    ...COMMON_WORKER_NAV_PATHS,
    ...COMMON_CARE_NAV_PATHS,
    '/dashboards/facility-ops',
    '/dashboards/quality',
    '/dashboards/workforce',
    '/residential',
    '/residential/admissions',
    '/residential/rooms',
    '/residential/occupancy',
    '/residential/evacuation-list',
    '/residential/care-minutes',
    '/residential/care-minutes/an-acc',
    '/residential/care-minutes/daily',
    '/finance/an-acc',
    '/finance/residential-billing',
    '/compliance/ipc',
    '/compliance/rn-coverage',
  ],
} as const satisfies Record<NonNullable<VisibleGoLiveScope>, readonly string[]>;

export type LaunchPrimaryNavigationLedgerEntry = {
  scope: NonNullable<VisibleGoLiveScope>;
  href: string;
};

export function getLaunchPrimaryNavigationPaths(scope: unknown): readonly string[] {
  const normalized = normalizeGoLiveScope(scope);
  return normalized ? LAUNCH_PRIMARY_NAV_PATHS[normalized] : [];
}

export function getLaunchPrimaryNavigationLedger(): LaunchPrimaryNavigationLedgerEntry[] {
  return Object.entries(LAUNCH_PRIMARY_NAV_PATHS).flatMap(([scope, hrefs]) =>
    hrefs.map((href) => ({ scope: scope as NonNullable<VisibleGoLiveScope>, href })),
  );
}

export function isLaunchPrimaryNavigationVisible(
  href: string,
  scope: unknown,
  options: { isDemoTenant?: boolean | null; includeDescendants?: boolean | null } = {},
): boolean {
  void href;
  void scope;
  void options;
  // Launch profiles are retained for setup/readiness reporting only. They must
  // not hide purchased modules from primary navigation for live providers.
  return true;
}
